import React, {useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import {endPoint} from '../../Helpers/EndPointHelpers';

const emptyForm = {id:null, name:'', groupId:'', channel:'email', subject:'', body:''};
const channelDetails = {
  email:{label:'Email', provider:'AWS SES', countKey:'eligible_email_contacts'},
  sms:{label:'SMS', provider:'Twilio', countKey:'eligible_sms_contacts'},
  push:{label:'Push', provider:'Rpush', countKey:'eligible_push_contacts'}
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const clampImageWidth = (value) => Math.min(600, Math.max(80, Number(value) || 480));

const completeHttpUrl = (value) => {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch(_error){
    return '';
  }
};

const formatDate = (value) => {
  if(!value) return 'No activity recorded';

  return new Intl.DateTimeFormat('en', {
    month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit'
  }).format(new Date(value));
};

const humanizeEvent = (value) => String(value || '').replace(/_/g, ' ');
const gateStatus = (gate) => {
  if(!gate.policy_enabled) return `Paused by kill switch · ${gate.blocked_reason}`;
  if(!gate.provider_ready) return `Enabled · ${gate.blocked_reason}`;
  return 'Enabled · provider ready';
};
const compactDigest = (value) => value ? `${value.slice(0, 12)}…` : '—';
const contentSignature = ({name, groupId, channel, subject, body}) => JSON.stringify({name, groupId, channel, subject, body});
const runContentSignature = (run) => contentSignature({
  name:run.name,
  groupId:String(run.audience.id),
  channel:run.channel,
  subject:run.subject || '',
  body:run.body || ''
});

const MessageComposer = ({
  messageRuns,
  selectedCompanyId,
  onMessageRuns,
  onRefresh,
  onRequestError,
  onError,
  onNotice
}) => {
  const quillRef = useRef(null);
  const previewFrameRef = useRef(null);
  const imageUploadInputRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [transitioningId, setTransitioningId] = useState(null);
  const [editorMode, setEditorMode] = useState('visual');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewWidth, setPreviewWidth] = useState(420);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [audiencePreviewLoading, setAudiencePreviewLoading] = useState(false);
  const [audiencePreviewError, setAudiencePreviewError] = useState('');
  const [testRecipients, setTestRecipients] = useState([]);
  const [selectedTestRecipientIds, setSelectedTestRecipientIds] = useState([]);
  const [testRecipientsLoading, setTestRecipientsLoading] = useState(false);
  const [testRecipientsRefresh, setTestRecipientsRefresh] = useState(0);
  const [testSending, setTestSending] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState(null);
  const [savedContentSignature, setSavedContentSignature] = useState('');
  const [linkedImage, setLinkedImage] = useState({imageUrl:'', linkUrl:'', alt:'', width:'480', placement:'center'});
  const [imageUploading, setImageUploading] = useState(false);
  const linkedImageWidth = clampImageWidth(linkedImage.width);
  const linkedImagePreviewUrl = completeHttpUrl(linkedImage.imageUrl);
  const linkedImagePreviewLink = completeHttpUrl(linkedImage.linkUrl);
  const hasUnsavedChanges = Boolean(form.id && savedContentSignature && contentSignature(form) !== savedContentSignature);
  const containsEmbeddedImage = /<img\b[^>]*\bsrc\s*=\s*["']data:image\//i.test(form.body);

  const selectedAudience = messageRuns?.audiences?.find((audience) => String(audience.id) === form.groupId);
  const channel = channelDetails[form.channel];
  const eligibleCount = audiencePreview?.counts?.eligible ?? selectedAudience?.[channel.countKey] ?? 0;
  const providerReadiness = typeof messageRuns?.summary?.delivery_ready === 'object' ?
    messageRuns.summary.delivery_ready :
    {email:Boolean(messageRuns?.summary?.delivery_ready), sms:false, push:false};
  const dispatchGates = messageRuns?.summary?.dispatch_gates;
  const hasServerDispatchGates = Boolean(dispatchGates?.channels);
  const gateForChannel = (channelKey) => dispatchGates?.channels?.[channelKey] || {
    policy_enabled:false,
    provider_ready:Boolean(providerReadiness[channelKey]),
    allowed:false,
    blocked_reason:'Server dispatch safeguards are unavailable; refresh before sending.'
  };
  const selectedChannelGate = gateForChannel(form.channel);
  const blockedChannels = Object.keys(channelDetails).filter((channelKey) => !gateForChannel(channelKey).allowed);
  const visibleRuns = (messageRuns?.message_runs || []).filter((run) => {
    if(statusFilter === 'all') return true;
    if(statusFilter === 'sent') return ['sent', 'completed'].includes(run.status);
    return run.status === statusFilter;
  });

  const quillModules = useMemo(() => ({
    toolbar:{
      container:[
        [{header:[1, 2, 3, false]}],
        ['bold', 'italic', 'underline', 'blockquote'],
        [{list:'ordered'}, {list:'bullet'}, {align:[]}],
        ['link', 'image'],
        ['clean']
      ],
      handlers:{
        image:() => imageUploadInputRef.current?.click()
      }
    }
  }), []);

  useEffect(() => {
    const frame = previewFrameRef.current;
    if(!frame || !window.ResizeObserver) return undefined;

    const observer = new window.ResizeObserver(([entry]) => {
      const nextWidth = Math.round(entry.target.getBoundingClientRect().width);
      if(nextWidth >= 300 && nextWidth <= 620){
        setPreviewWidth((current) => current === nextWidth ? current : nextWidth);
      }
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    setAudiencePreview(null);
    setAudiencePreviewError('');

    if(!selectedCompanyId || !form.groupId){
      setAudiencePreviewLoading(false);
      return () => { active = false; };
    }

    setAudiencePreviewLoading(true);
    const params = new URLSearchParams({
      company_id:String(selectedCompanyId),
      group_id:form.groupId,
      channel:form.channel
    });

    axios.get(
      `${endPoint()}/api/campaign_manager/audience_preview?${params.toString()}`,
      {withCredentials:true}
    ).then(({data}) => {
      if(!active) return;
      setAudiencePreview(data);
      setAudiencePreviewLoading(false);
    }).catch((requestError) => {
      if(!active) return;
      setAudiencePreviewLoading(false);
      setAudiencePreviewError('Audience preview is unavailable. Try selecting the group again.');
      if([401, 403].includes(requestError?.response?.status)) onRequestError(requestError);
    });

    return () => { active = false; };
  }, [form.channel, form.groupId, onRequestError, selectedCompanyId]);

  useEffect(() => {
    let active = true;
    setTestRecipients([]);
    setSelectedTestRecipientIds([]);

    if(!selectedCompanyId || !form.id){
      setTestRecipientsLoading(false);
      return () => { active = false; };
    }

    setTestRecipientsLoading(true);
    const params = new URLSearchParams({company_id:String(selectedCompanyId)});
    axios.get(
      `${endPoint()}/api/campaign_manager/message_runs/${form.id}/test_recipients?${params.toString()}`,
      {withCredentials:true}
    ).then(({data}) => {
      if(!active) return;
      setTestRecipients(data.recipients || []);
      setTestRecipientsLoading(false);
    }).catch((requestError) => {
      if(!active) return;
      setTestRecipientsLoading(false);
      if([401, 403].includes(requestError?.response?.status)) onRequestError(requestError);
      else onError('Test recipients are unavailable for this saved message.');
    });

    return () => { active = false; };
  }, [form.id, onError, onRequestError, selectedCompanyId, testRecipientsRefresh]);

  const previewDocument = useMemo(() => {
    let content = form.body || '<p style="color:#7b8790">Your message preview will appear here.</p>';
    if(form.channel !== 'email') content = `<p>${escapeHtml(form.body).replace(/\n/g, '<br>')}</p>`;

    return `<!doctype html><html><head><meta charset="utf-8"><style>
      *{box-sizing:border-box}body{margin:0;padding:28px;color:#172433;background:#fff;font:15px/1.55 Arial,sans-serif}
      img{max-width:100%;height:auto}a{color:#087f78}h1,h2,h3{line-height:1.2}
      .subject{margin:0 0 22px;padding-bottom:14px;border-bottom:1px solid #e1e7e9;font-size:18px}
      .push-card{padding:15px;border:1px solid #dfe6e9;border-radius:14px;box-shadow:0 8px 22px rgba(23,59,87,.12)}
      .sms-card{padding:14px 16px;background:#e7f2f0;border-radius:18px 18px 4px 18px}
    </style></head><body>
      ${form.channel === 'email' ? `<div class="subject">${escapeHtml(form.subject || 'Email subject')}</div>${content}` : ''}
      ${form.channel === 'sms' ? `<div class="sms-card">${content}</div>` : ''}
      ${form.channel === 'push' ? `<div class="push-card"><strong>${escapeHtml(form.subject || 'Push title')}</strong>${content}</div>` : ''}
    </body></html>`;
  }, [form.body, form.channel, form.subject]);

  const updateFormFromEvent = (field) => (event) => {
    const value = event.currentTarget.value;
    setForm((current) => ({...current, [field]:value}));
    onNotice('');
  };

  const updateBody = (body) => {
    setForm((current) => ({...current, body}));
    onNotice('');
  };

  const updatePreviewWidth = (event) => setPreviewWidth(Number(event.currentTarget.value));

  const chooseChannel = (nextChannel) => {
    if(nextChannel === form.channel) return;

    setForm((current) => ({...emptyForm, name:current.name, groupId:current.groupId, channel:nextChannel}));
    setSavedContentSignature('');
    setEditorMode('visual');
    onError('');
    onNotice('');
  };

  const handleValidationError = (requestError) => {
    const validationErrors = requestError?.response?.data?.errors;
    setSaving(false);
    setTransitioningId(null);
    setTestSending(false);
    setImageUploading(false);
    if(validationErrors?.length){
      onError(validationErrors.join(' '));
      return;
    }
    onRequestError(requestError);
  };

  const saveDraft = (event) => {
    event.preventDefault();
    if(containsEmbeddedImage){
      onError('The email contains an embedded image. Upload it to AWS S3 before saving.');
      return;
    }
    setSaving(true);
    onNotice('');
    onError('');

    const params = new URLSearchParams({company_id:selectedCompanyId});
    const payload = {
      message_run:{
        name:form.name.trim(),
        group_id:form.groupId,
        channel:form.channel,
        subject:form.subject.trim(),
        body:form.body.trim()
      }
    };
    const request = form.id ?
      axios.patch(`${endPoint()}/api/campaign_manager/message_runs/${form.id}?${params.toString()}`, payload, {withCredentials:true}) :
      axios.post(`${endPoint()}/api/campaign_manager/message_runs?${params.toString()}`, payload, {withCredentials:true});

    request.then(({data}) => {
      onMessageRuns(data);
      const saved = form.id ? data.message_runs.find((run) => run.id === form.id) : data.message_runs[0];
      const savedForm = saved ? {
        id:saved.id,
        name:saved.name,
        groupId:String(saved.audience.id),
        channel:saved.channel,
        subject:saved.subject || '',
        body:saved.body || ''
      } : form;
      setForm(savedForm);
      if(saved) setSavedContentSignature(contentSignature(savedForm));
      setTestRecipientsRefresh((current) => current + 1);
      onNotice('Draft saved. No message has been sent. Mark it ready when review is complete.');
      setSaving(false);
    }).catch(handleValidationError);
  };

  const editRun = (run) => {
    setForm({
      id:run.id,
      name:run.name,
      groupId:String(run.audience.id),
      channel:run.channel,
      subject:run.subject || '',
      body:run.body || ''
    });
    setEditorMode(run.channel === 'email' ? 'visual' : 'plain');
    setSavedContentSignature(runContentSignature(run));
    onError('');
    onNotice(run.status === 'ready' ? 'Editing this message will return it to Draft.' : 'Draft loaded for editing.');
    window.scrollTo({top:0, behavior:'smooth'});
  };

  const newDraft = () => {
    setForm(emptyForm);
    setSavedContentSignature('');
    setEditorMode('visual');
    onError('');
    onNotice('');
  };

  const markReady = (run) => {
    const confirmed = window.confirm(
      `Approve “${run.name}” and freeze its content plus ${run.estimated_recipient_count} currently eligible contacts? ` +
      'Editing after approval will invalidate this snapshot.'
    );
    if(!confirmed) return;

    setTransitioningId(run.id);
    onError('');
    onNotice('');
    const params = new URLSearchParams({company_id:selectedCompanyId});

    axios.post(
      `${endPoint()}/api/campaign_manager/message_runs/${run.id}/ready?${params.toString()}`,
      {},
      {withCredentials:true}
    ).then(({data}) => {
      onMessageRuns(data);
      const approvedRun = data.message_runs.find((messageRun) => messageRun.id === run.id);
      onNotice(
        `“${run.name}” is approved with an immutable ${approvedRun?.approval?.recipient_count || 0}-contact audience snapshot.`
      );
      setTransitioningId(null);
    }).catch(handleValidationError);
  };

  const dispatchRun = (run) => {
    const provider = channelDetails[run.channel].provider;
    const approval = run.approval;
    const confirmed = window.confirm(
      `Send approved snapshot ${compactDigest(approval?.audience_digest)} for “${run.name}” to ` +
      `${approval?.recipient_count || 0} contacts through ${provider}? Newly ineligible contacts will be suppressed; no new contacts can be added.`
    );
    if(!confirmed) return;

    setTransitioningId(run.id);
    onError('');
    onNotice('');
    const params = new URLSearchParams({company_id:selectedCompanyId});

    axios.post(
      `${endPoint()}/api/campaign_manager/message_runs/${run.id}/dispatch?${params.toString()}`,
      {expected_approval_id:approval?.id},
      {withCredentials:true}
    ).then(({data}) => {
      onMessageRuns(data);
      onNotice(`Batch queued through ${provider}. Delivery metrics will update as provider events arrive.`);
      setTransitioningId(null);
    }).catch(handleValidationError);
  };

  const toggleTestRecipient = (recipientId) => {
    setSelectedTestRecipientIds((current) => {
      if(current.includes(recipientId)) return current.filter((id) => id !== recipientId);
      if(current.length >= 5) return current;
      return [...current, recipientId];
    });
  };

  const sendTests = () => {
    if(!form.id || !selectedTestRecipientIds.length) return;
    const confirmed = window.confirm(
      `Send a real ${channel.label} test to ${selectedTestRecipientIds.length} selected ${selectedTestRecipientIds.length === 1 ? 'contact' : 'contacts'}? ` +
      'Test activity is audited but excluded from campaign metrics.'
    );
    if(!confirmed) return;

    setTestSending(true);
    onError('');
    onNotice('');
    const params = new URLSearchParams({company_id:selectedCompanyId});
    axios.post(
      `${endPoint()}/api/campaign_manager/message_runs/${form.id}/test_sends?${params.toString()}`,
      {user_ids:selectedTestRecipientIds},
      {withCredentials:true}
    ).then(({data}) => {
      onMessageRuns(data);
      onNotice(
        `${selectedTestRecipientIds.length} ${channel.label} test ${selectedTestRecipientIds.length === 1 ? 'send' : 'sends'} queued. Campaign metrics are unchanged.`
      );
      setSelectedTestRecipientIds([]);
      setTestSending(false);
    }).catch(handleValidationError);
  };

  const uploadImage = (event) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if(!file) return;
    if(!['image/jpeg', 'image/png'].includes(file.type)){
      onError('Choose a PNG or JPEG image.');
      return;
    }
    if(file.size > 5 * 1024 * 1024){
      onError('The image is too large (maximum is 5 MB).');
      return;
    }

    const payload = new FormData();
    payload.append('file', file, file.name);
    const params = new URLSearchParams({company_id:selectedCompanyId});
    setImageUploading(true);
    onError('');
    onNotice('Uploading and optimizing the image in AWS S3…');

    axios.post(
      `${endPoint()}/api/campaign_manager/email_assets?${params.toString()}`,
      payload,
      {withCredentials:true, timeout:30000}
    ).then(({data}) => {
      setLinkedImage((current) => ({...current, imageUrl:data.asset.url}));
      setImageUploading(false);
      onNotice('Image uploaded to AWS S3. Choose its link, alt text, width, and placement, then insert it.');
    }).catch(handleValidationError);
  };

  const addLinkedImage = () => {
    let image;
    let link;
    try {
      image = new URL(linkedImage.imageUrl);
      if(!['http:', 'https:'].includes(image.protocol)) throw new Error('invalid image protocol');
      if(linkedImage.linkUrl){
        link = new URL(linkedImage.linkUrl);
        if(!['http:', 'https:'].includes(link.protocol)) throw new Error('invalid link protocol');
      }
    } catch(_error){
      onError('Image and link URLs must be complete http:// or https:// addresses.');
      return;
    }

    const width = linkedImageWidth;
    const imageHtml = `<img src="${escapeHtml(image.href)}" alt="${escapeHtml(linkedImage.alt)}" width="${width}" style="width:${width}px;max-width:100%;height:auto">`;
    const linkedHtml = link ? `<a href="${escapeHtml(link.href)}" target="_blank" rel="noopener noreferrer">${imageHtml}</a>` : imageHtml;
    const html = `<p style="text-align:${linkedImage.placement}">${linkedHtml}</p>`;
    const editor = quillRef.current?.getEditor();

    if(editor){
      const selection = editor.getSelection(true) || {index:editor.getLength()};
      editor.clipboard.dangerouslyPasteHTML(selection.index, html, 'user');
    } else {
      updateBody(`${form.body}${html}`);
    }
    setLinkedImage({imageUrl:'', linkUrl:'', alt:'', width:'480', placement:'center'});
    onError('');
  };

  return (
    <>
      <section className="campaign-manager-lifecycle" aria-label="Message lifecycle">
        {['draft', 'ready', 'sent'].map((status, index) => (
          <button className={statusFilter === status ? 'is-active' : ''} key={status} onClick={() => setStatusFilter(status)} type="button">
            <span>0{index + 1}</span>
            <strong>{status}</strong>
            <small>{status === 'sent' ? messageRuns?.summary?.sent || 0 : messageRuns?.summary?.[`${status}s`] || messageRuns?.summary?.[status] || 0}</small>
          </button>
        ))}
        <button className={statusFilter === 'all' ? 'is-active' : ''} onClick={() => setStatusFilter('all')} type="button">
          <span>••</span><strong>All</strong><small>{messageRuns?.message_runs?.length || 0}</small>
        </button>
      </section>

      <section className={`campaign-manager-dispatch-gates${blockedChannels.length ? ' is-blocked' : ''}`} aria-label="Dispatch safeguards">
        <div>
          <p className="campaign-manager-eyebrow">Dispatch safeguards</p>
          <strong>{blockedChannels.length ? 'One or more channels are paused' : 'All configured channels are available'}</strong>
          <small>
            {hasServerDispatchGates ?
              `Server enforcement active · company kill switch ${dispatchGates.company_enabled === false ? 'paused' : 'enabled'} · audience ceiling ${dispatchGates.max_audience_size.toLocaleString()}` :
              'Server enforcement unavailable · sending paused until policy status is loaded'}
          </small>
          <small>Policy, audience, and provider readiness are rechecked immediately before every provider call. If a gate closes mid-run, unsent recipients remain queued in a Paused run and an audit event is recorded.</small>
        </div>
        <dl>
          {Object.entries(channelDetails).map(([channelKey, details]) => {
            const gate = gateForChannel(channelKey);
            return <div key={channelKey}><dt>{details.label}</dt><dd title={gate.blocked_reason || ''}>{gateStatus(gate)}</dd></div>;
          })}
        </dl>
      </section>

      <div className="campaign-manager-composer-layout">
        <section className="campaign-manager-compose-card">
          <div className="campaign-manager-compose-card__header">
            <div>
              <p className="campaign-manager-eyebrow">{form.id ? `Editing message ${form.id}` : 'New message run'}</p>
              <h2>{channel.label} draft</h2>
            </div>
            <div className="campaign-manager-compose-card__actions">
              {form.id ? <button onClick={newDraft} type="button">New draft</button> : null}
              <span className="campaign-manager-provider">{channel.provider}</span>
            </div>
          </div>

          <div className="campaign-manager-channel-picker" aria-label="Communication channel">
            {Object.entries(channelDetails).map(([key, details]) => (
              <button className={form.channel === key ? 'is-active' : ''} key={key} onClick={() => chooseChannel(key)} type="button">
                {details.label}
              </button>
            ))}
          </div>

          <form className="campaign-manager-compose-form" onSubmit={saveDraft}>
            <div className="campaign-manager-form-row">
              <label>
                <span>Internal draft name</span>
                <input maxLength="255" onChange={updateFormFromEvent('name')} placeholder="e.g. Week 2 enrollment reminder" required type="text" value={form.name} />
              </label>
              <label>
                <span>Target group</span>
                <select onChange={updateFormFromEvent('groupId')} required value={form.groupId}>
                  <option value="">Select an HSC group</option>
                  {messageRuns?.audiences?.map((audience) => (
                    <option key={audience.id} value={audience.id}>
                      {audience.name} · {audience[channel.countKey]} eligible
                    </option>
                  ))}
                </select>
                <small>{selectedAudience ? `${eligibleCount} consented contacts currently eligible for ${channel.label}.` : 'Audience eligibility is rechecked before every delivery.'}</small>
              </label>
            </div>

            {selectedAudience ?
              <section className="campaign-manager-audience-preview" aria-label="Audience preview" aria-live="polite">
                <div className="campaign-manager-audience-preview__header">
                  <div>
                    <strong>Audience preview</strong>
                    <small>{selectedAudience.name} · {channel.label} through {channel.provider}</small>
                  </div>
                  <span>{audiencePreviewLoading ? 'Checking…' : 'Live eligibility'}</span>
                </div>
                {audiencePreviewLoading ?
                  <p>Calculating consent, suppression, and channel reachability…</p>
                  : audiencePreviewError ?
                    <p className="campaign-manager-audience-preview__error">{audiencePreviewError}</p>
                    : audiencePreview ?
                      <>
                        <dl className="campaign-manager-audience-preview__metrics">
                          <div><dt>Targeted</dt><dd>{audiencePreview.counts.targeted}</dd></div>
                          <div><dt>Eligible</dt><dd>{audiencePreview.counts.eligible}</dd></div>
                          <div><dt>Excluded</dt><dd>{audiencePreview.counts.excluded}</dd></div>
                          <div><dt>Duplicates removed</dt><dd>{audiencePreview.counts.duplicates_removed}</dd></div>
                        </dl>
                        <div className="campaign-manager-audience-preview__categories">
                          <span><strong>{audiencePreview.counts.consent_excluded}</strong> consent excluded</span>
                          <span><strong>{audiencePreview.counts.suppressed}</strong> suppressed</span>
                          <span><strong>{audiencePreview.counts.unreachable}</strong> unreachable</span>
                        </div>
                        {audiencePreview.exclusion_reasons.length ?
                          <ul>
                            {audiencePreview.exclusion_reasons.map((reason) => (
                              <li key={reason.key}><span>{reason.label}</span><strong>{reason.count}</strong></li>
                            ))}
                          </ul>
                          : <p>Every targeted contact is currently eligible for this channel.</p>}
                        <small className="campaign-manager-audience-preview__caveat">{audiencePreview.caveat}</small>
                      </>
                      : null}
              </section>
              : null}

            {form.channel !== 'sms' ?
              <label>
                <span>{form.channel === 'push' ? 'Push title' : 'Subject'}</span>
                <input maxLength="998" onChange={updateFormFromEvent('subject')} placeholder={form.channel === 'push' ? 'Notification title' : 'Email subject'} required type="text" value={form.subject} />
              </label>
              : null}

            {form.channel === 'email' ?
              <div className="campaign-manager-email-editor">
                <div className="campaign-manager-editor-tabs">
                  <span>Message body</span>
                  <button className={editorMode === 'visual' ? 'is-active' : ''} onClick={() => setEditorMode('visual')} type="button">Visual</button>
                  <button className={editorMode === 'html' ? 'is-active' : ''} onClick={() => setEditorMode('html')} type="button">HTML</button>
                </div>
                {editorMode === 'visual' ?
                  <ReactQuill
                    formats={['header', 'bold', 'italic', 'underline', 'blockquote', 'list', 'bullet', 'align', 'link', 'image']}
                    modules={quillModules}
                    onChange={updateBody}
                    placeholder="Write the email message…"
                    ref={quillRef}
                    theme="snow"
                    value={form.body}
                  />
                  :
                  <textarea className="campaign-manager-html-source" maxLength="100000" onChange={updateFormFromEvent('body')} placeholder="Paste or author HTML…" rows="14" value={form.body} />}

                <small className={`campaign-manager-email-editor__size${containsEmbeddedImage ? ' is-over-limit' : ''}`}>
                  {form.body.length.toLocaleString()} / 100,000 HTML characters. Images uploaded to AWS S3 use short URLs.
                </small>
                {containsEmbeddedImage ?
                  <p className="campaign-manager-email-editor__warning" role="alert">
                    This email contains embedded image data. Remove it, then use the AWS S3 upload below.
                  </p>
                  : null}

                <div className="campaign-manager-image-builder">
                  <div className="campaign-manager-image-builder__intro">
                    <div>
                      <strong>AWS S3 image</strong>
                      <small>Upload a PNG or JPEG, or add a complete HTTPS image URL, then configure its placement and link.</small>
                    </div>
                    <input
                      accept="image/jpeg,image/png"
                      aria-label="Upload image from computer"
                      className="campaign-manager-image-builder__file"
                      onChange={uploadImage}
                      ref={imageUploadInputRef}
                      type="file"
                    />
                    <button disabled={imageUploading} onClick={() => imageUploadInputRef.current?.click()} type="button">
                      {imageUploading ? 'Uploading to S3…' : 'Upload image to S3'}
                    </button>
                  </div>
                  <div className="campaign-manager-image-builder__fields">
                    <label>
                      <span>Image URL</span>
                      <input onChange={updateFormFromEventForImage(setLinkedImage, 'imageUrl')} placeholder="https://…/image.png" type="url" value={linkedImage.imageUrl} />
                    </label>
                    <label>
                      <span>Optional link</span>
                      <input onChange={updateFormFromEventForImage(setLinkedImage, 'linkUrl')} placeholder="https://…" type="url" value={linkedImage.linkUrl} />
                    </label>
                    <label>
                      <span>Alt text</span>
                      <input onChange={updateFormFromEventForImage(setLinkedImage, 'alt')} placeholder="Describe the image" type="text" value={linkedImage.alt} />
                    </label>
                    <label>
                      <span>Image width (px)</span>
                      <input disabled={!linkedImagePreviewUrl} max="600" min="80" onChange={updateFormFromEventForImage(setLinkedImage, 'width')} type="number" value={linkedImage.width} />
                    </label>
                    <label>
                      <span>Placement</span>
                      <select disabled={!linkedImagePreviewUrl} onChange={updateFormFromEventForImage(setLinkedImage, 'placement')} value={linkedImage.placement}>
                        <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                      </select>
                    </label>
                    <button disabled={!linkedImagePreviewUrl} onClick={addLinkedImage} type="button">Insert image</button>
                  </div>
                  <div className="campaign-manager-image-builder__preview" aria-live="polite">
                    <div><strong>Insertion preview</strong><output>{linkedImageWidth}px</output></div>
                    <div style={{textAlign:linkedImage.placement}}>
                      {linkedImagePreviewUrl ?
                        linkedImagePreviewLink ?
                          <a href={linkedImagePreviewLink} rel="noopener noreferrer" target="_blank">
                            <img alt={linkedImage.alt} src={linkedImagePreviewUrl} style={{width:`${linkedImageWidth}px`}} />
                          </a> :
                          <img alt={linkedImage.alt} src={linkedImagePreviewUrl} style={{width:`${linkedImageWidth}px`}} />
                        :
                        <small>Enter an image URL to preview its width and placement before inserting it.</small>}
                    </div>
                  </div>
                </div>
              </div>
              :
              <label>
                <span>{form.channel === 'sms' ? 'SMS message' : 'Push message'}</span>
                <textarea maxLength={form.channel === 'sms' ? 1600 : 4096} onChange={updateFormFromEvent('body')} placeholder={`Write the ${channel.label} message…`} required rows="8" value={form.body} />
                <small>{form.body.length} / {form.channel === 'sms' ? '1,600' : '4,096'} characters. Do not include PHI in SMS or push content.</small>
              </label>}

            <div className="campaign-manager-compose-form__footer">
              <p>Saving creates an auditable Draft only. Review it in the preview, then move it to Ready before sending.</p>
              <button disabled={saving || imageUploading || containsEmbeddedImage} type="submit">{saving ? 'Saving…' : form.id ? 'Update draft' : 'Save draft'}</button>
            </div>
          </form>

          <section className="campaign-manager-test-send" aria-label="Test sends">
            <div className="campaign-manager-test-send__header">
              <div><strong>Test send</strong><small>Saved content · eligible members of the selected HSC group</small></div>
              <span>Maximum 5</span>
            </div>
            {!form.id ?
              <p>Save the draft to select masked test recipients.</p>
              : hasUnsavedChanges ?
                <p>Save your current changes before sending a test so the preview and provider content match.</p>
              : testRecipientsLoading ?
                <p>Loading eligible test recipients…</p>
                : testRecipients.length ?
                  <>
                    <div className="campaign-manager-test-send__recipients">
                      {testRecipients.map((recipient) => (
                        <label key={recipient.id}>
                          <input
                            checked={selectedTestRecipientIds.includes(recipient.id)}
                            disabled={!selectedTestRecipientIds.includes(recipient.id) && selectedTestRecipientIds.length >= 5}
                            onChange={() => toggleTestRecipient(recipient.id)}
                            type="checkbox"
                          />
                          <span><strong>{recipient.operational_label}</strong><small>{recipient.destination}</small></span>
                        </label>
                      ))}
                    </div>
                    <button
                      disabled={hasUnsavedChanges || !selectedChannelGate.allowed || !selectedTestRecipientIds.length || testSending}
                      onClick={sendTests}
                      title={selectedChannelGate.blocked_reason || ''}
                      type="button"
                    >
                      {testSending ? 'Queueing tests…' : `Send ${selectedTestRecipientIds.length || ''} test${selectedTestRecipientIds.length === 1 ? '' : 's'}`}
                    </button>
                  </>
                  : <p>No contacts in this group are currently eligible for a {channel.label} test.</p>}
          </section>
        </section>

        <aside className="campaign-manager-preview">
          <div className="campaign-manager-preview__header">
            <div><p className="campaign-manager-eyebrow">Live preview</p><strong>{channel.label}</strong></div>
            <div className="campaign-manager-preview__controls">
              <label>
                <span>Preview width</span>
                <span className="campaign-manager-preview__width-control">
                  <input aria-label="Preview width" max="620" min="300" onChange={updatePreviewWidth} onInput={updatePreviewWidth} type="range" value={previewWidth} />
                  <output>{previewWidth}px</output>
                </span>
              </label>
              <div className="campaign-manager-preview__presets" aria-label="Preview width presets">
                {[300, 420, 620].map((width) => (
                  <button aria-pressed={previewWidth === width} key={width} onClick={() => setPreviewWidth(width)} type="button">{width}px</button>
                ))}
              </div>
            </div>
          </div>
          <div className="campaign-manager-preview__stage">
            <div className="campaign-manager-preview__frame" ref={previewFrameRef} style={{width:`${previewWidth}px`}}>
              <iframe sandbox="" srcDoc={previewDocument} title={`${channel.label} message preview`} />
            </div>
          </div>
          <p>Use the width control or drag the frame corner to test responsive layouts.</p>
        </aside>
      </div>

      <section className="campaign-manager-draft-list campaign-manager-message-runs">
        <div className="campaign-manager-draft-list__header">
          <div><p className="campaign-manager-eyebrow">Recent activity</p><h2>{statusFilter === 'all' ? 'All message runs' : `${statusFilter} messages`}</h2></div>
          <div className="campaign-manager-draft-list__actions">
            <button onClick={onRefresh} type="button">Refresh</button>
            <strong>{visibleRuns.length}</strong>
          </div>
        </div>
        <div className="campaign-manager-run-grid">
          {visibleRuns.length ? visibleRuns.map((run) => (
            <article key={run.id}>
              <div><span>{run.channel}</span><span>{channelDetails[run.channel].provider}</span><span className={`campaign-manager-status campaign-manager-status--${run.status}`}>{run.status === 'completed' ? 'sent' : run.status}</span></div>
              <h3>{run.name}</h3>
              <p>{run.subject || run.body}</p>
              <dl>
                <div><dt>Audience</dt><dd>{run.audience.name}</dd></div>
                <div><dt>Eligible snapshot</dt><dd>{run.estimated_recipient_count}</dd></div>
                <div><dt>Saved</dt><dd>{formatDate(run.created_at)}</dd></div>
              </dl>
              {run.approval ?
                <section className="campaign-manager-approval-summary" aria-label="Approval snapshot">
                  <div><strong>Approved snapshot</strong><span>{formatDate(run.approval.approved_at)}</span></div>
                  <dl>
                    <div><dt>Approver</dt><dd>{run.approval.approved_by.operational_label}</dd></div>
                    <div><dt>Frozen contacts</dt><dd>{run.approval.recipient_count}</dd></div>
                    <div><dt>Audience hash</dt><dd title={run.approval.audience_digest}>{compactDigest(run.approval.audience_digest)}</dd></div>
                    <div><dt>Content hash</dt><dd title={run.approval.content_digest}>{compactDigest(run.approval.content_digest)}</dd></div>
                  </dl>
                </section>
                : null}
              {!['draft', 'ready'].includes(run.status) ?
                <div className="campaign-manager-run-metrics">
                  <span><strong>{run.metrics.sent}</strong> sent</span>
                  <span><strong>{run.metrics.delivered}</strong> delivered</span>
                  {run.channel === 'email' ? <><span><strong>{run.metrics.opened}</strong> opened</span><span><strong>{run.metrics.clicked}</strong> clicked</span><span><strong>{run.metrics.bounced}</strong> bounced</span><span><strong>{run.metrics.complained}</strong> complaints</span></> : null}
                  <span><strong>{run.metrics.failed}</strong> failed</span>
                  <span><strong>{run.metrics.suppressed}</strong> suppressed</span>
                </div>
                : null}
              {run.status === 'draft' ?
                <div className="campaign-manager-run-actions">
                  <button onClick={() => editRun(run)} type="button">Edit</button>
                  <button
                    disabled={!gateForChannel(run.channel).policy_enabled || transitioningId === run.id}
                    onClick={() => markReady(run)}
                    title={!gateForChannel(run.channel).policy_enabled ? gateForChannel(run.channel).blocked_reason : ''}
                    type="button"
                  >
                    {transitioningId === run.id ? 'Approving…' : 'Approve snapshot'}
                  </button>
                </div>
                : null}
              {run.status === 'ready' ?
                <div className="campaign-manager-run-actions">
                  <button onClick={() => editRun(run)} type="button">Edit</button>
                  <button
                    disabled={!gateForChannel(run.channel).allowed || !run.approval || transitioningId === run.id}
                    onClick={() => dispatchRun(run)}
                    title={gateForChannel(run.channel).blocked_reason || ''}
                    type="button"
                  >
                    {transitioningId === run.id ? 'Queueing…' : `Send approved ${run.approval?.recipient_count || 0}`}
                  </button>
                </div>
                : null}
              <button
                className="campaign-manager-audit-toggle"
                onClick={() => setExpandedAuditId((current) => current === run.id ? null : run.id)}
                type="button"
              >
                {expandedAuditId === run.id ? 'Hide audit' : `Audit · ${run.audit_events?.length || 0}`}
              </button>
              {expandedAuditId === run.id ?
                <section className="campaign-manager-audit-trail" aria-label={`${run.name} audit history`}>
                  {run.test_sends?.length ?
                    <div className="campaign-manager-test-history">
                      <strong>Recent tests</strong>
                      {run.test_sends.map((testSend) => (
                        <span key={testSend.id}>{testSend.recipient.operational_label} · {testSend.recipient.destination} · {testSend.status}</span>
                      ))}
                    </div>
                    : null}
                  {run.audit_events?.length ? run.audit_events.map((event) => (
                    <div key={event.id}>
                      <span>{humanizeEvent(event.event_type)}</span>
                      <strong>{event.actor.operational_label}</strong>
                      <small>{formatDate(event.created_at)}</small>
                    </div>
                  )) : <p>No workflow events recorded.</p>}
                </section>
                : null}
            </article>
          )) : <div className="campaign-manager-empty">No {statusFilter === 'all' ? '' : `${statusFilter} `}message runs for this study.</div>}
        </div>
      </section>
    </>
  );
};

const updateFormFromEventForImage = (setter, field) => (event) => {
  const value = event.currentTarget.value;
  setter((current) => ({...current, [field]:value}));
};

export default MessageComposer;
